import React from 'react';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import Close from '@material-ui/icons/Close';
import Check from '@material-ui/icons/Check';
import jsonExport from 'jsonexport/dist';
import convertDistance from 'geolib/es/convertDistance';
import getPathLength from 'geolib/es/getPathLength';
import {
    List,
    Datagrid,
    TextField,
    ReferenceField,
    DateField,
    Pagination,
    DateInput,
    AutocompleteInput,
    Filter,
    Show,
    TextInput,
    SimpleShowLayout,
    FunctionField,
    downloadCSV,
    ReferenceInput,
    SelectInput,
    DeleteButton,
    BulkDeleteButton,
} from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DateTime } from 'luxon';

const ISRAEL_TZ = 'Asia/Jerusalem';

/** Strip fractional seconds so display is HH:mm:ss (e.g. "04:55:05.356573" -> "04:55:05"). */
const normalizeTimeToHHmmss = (timeStr) => {
    if (timeStr == null || timeStr === '') return '';
    const s = String(timeStr).trim();
    const parts = s.split(':');
    if (parts.length < 3) return s;
    const secPart = parts[2].includes('.') ? parts[2].split('.')[0] : parts[2];
    return `${parts[0]}:${parts[1]}:${secPart}`;
};

const toIsraelTime = (recordOrStartTime, maybeDate, maybeTimeZone) => {
    // Support both (record) and (start_time, date, time_zone) call styles
    let start_time;
    let date;
    let time_zone;
    if (typeof recordOrStartTime === 'object' && recordOrStartTime !== null && !maybeDate) {
        const record = recordOrStartTime;
        start_time = record.start_time;
        date = record.date;
        time_zone = record.time_zone;
    } else {
        start_time = recordOrStartTime;
        date = maybeDate;
        time_zone = maybeTimeZone;
    }

    if (!start_time || !date) return '';

    try {
        const [year, month, day] = String(date).split('-').map(Number);
        const [hRaw, mRaw, sRaw] = String(start_time).split(':');
        const h = Number.isFinite(Number(hRaw)) ? Number(hRaw) : 0;
        const m = Number.isFinite(Number(mRaw)) ? Number(mRaw) : 0;
        const sInt = sRaw != null && sRaw.includes('.') ? sRaw.split('.')[0] : sRaw;
        const s = Number.isFinite(Number(sInt)) ? Math.floor(Number(sInt)) : 0;

        const sourceZone = time_zone || ISRAEL_TZ;
        const dt = DateTime.fromObject(
            { year, month, day, hour: h, minute: m, second: s },
            { zone: sourceZone }
        );
        if (!dt.isValid) {
            return normalizeTimeToHHmmss(String(start_time));
        }
        const israel = dt.setZone(ISRAEL_TZ);
        if (!israel.isValid) {
            return normalizeTimeToHHmmss(String(start_time));
        }
        return israel.toFormat('HH:mm:ss');
    } catch (e) {
        // Fallback to raw start_time on any parsing / conversion issue
        try {
            // Best-effort logging; ignore if console is not available in some environments
            // eslint-disable-next-line no-console
            console.error('Failed to convert start_time to Israel time', {
                error: e?.message,
                start_time,
                date,
                time_zone,
            });
        } catch (_) {}
        return normalizeTimeToHHmmss(String(start_time));
    }
};

// Restrict locations to the timestamps between the first and last
// station markers, so we ignore GPS noise before START and after
// FINISH when calculating distance.
const windowLocationsByTimestamp = (locations) => {
    if (!Array.isArray(locations)) return [];

    const stationPoints = locations.filter(
        (p) =>
            p &&
            p.type === 'station' &&
            typeof p.timestamp === 'number'
    );
    if (stationPoints.length < 2) {
        return locations;
    }

    const startTs = stationPoints[0].timestamp;
    const endTs = stationPoints[stationPoints.length - 1].timestamp;

    return locations.filter(
        (p) =>
            p &&
            typeof p.timestamp === 'number' &&
            p.timestamp >= startTs &&
            p.timestamp <= endTs
    );
};

// Calculate total distance (km) from locations array, mirroring the
// mobile app logic: only "location" points, apply accuracy filter,
// and restrict to the station timestamp window.
const calculateDistanceFromLocations = (locations, accuracyThreshold) => {
    if (!Array.isArray(locations) || locations.length === 0) return null;

    const windowed = windowLocationsByTimestamp(locations);

    const threshold =
        typeof accuracyThreshold === 'number' && isFinite(accuracyThreshold) && accuracyThreshold > 0
            ? accuracyThreshold
            : null;

    const filtered = [];
    windowed.forEach((point) => {
        if (!point || point.type !== 'location') return;
        
        // Require a numeric timestamp - ignore points without one
        if (typeof point.timestamp !== 'number') return;

        if (threshold !== null) {
            let acc = null;
            if (point.accuracy !== undefined && point.accuracy !== null) {
                const parsed = Number(point.accuracy);
                if (isFinite(parsed)) {
                    acc = parsed;
                }
            }
            // Skip if accuracy is missing or too bad
            if (acc === null || acc >= threshold) return;
        }

        filtered.push(point);
    });

    if (filtered.length < 2) return null;

    const meters = getPathLength(filtered);
    if (!isFinite(meters)) {
        return null;
    }

    return Number(convertDistance(meters, 'km').toFixed(3));
};

const exporter = (activities, permissions) => {
    const safe = (v) => (v === null || v === undefined ? '' : v);
    const activitiesForExport = activities.map(activity => {
        const {
            track,
            status,
            total_score,
            start_time,
            total_time,
            total_distance,
            rate,
            user,
            user_full_name,
            user_email,
            user_phone,
            date,
            track_title,
            is_all_stations,
            stations,
            locations,
            arrivals,
            id,
            is_ordered,
            arrivals_count,
            code,
            location_accuracy,
            time_zone,
            time_zone_offset, // kept for backwards-compat/debugging if backend sends it
            ...row
        } = activity;
        row.TotalTime = toTimeFormat(Number(total_time || 0));
        row.UserName = safe(user_full_name);
        // Only include email and phone for admin users
        if (permissions === 'admin') {
            row.Email = safe(user_email);
            row.Phone = safe(user_phone);
        }
        row.CourseName = safe(track_title);
        row.Status = safe(status);
        // If hard time limit exceeded and status is InProgress, show as DNF in export
        if (status === 'InProgress' && track && track.route_max_time) {
            const hardTimeLimitSeconds = track.route_max_time * 60;
            if (total_time >= hardTimeLimitSeconds) {
                row.Status = 'DNF';
            }
        }
        const israelTime = toIsraelTime(start_time, date, time_zone);
        row.StartTime = israelTime || normalizeTimeToHHmmss(safe(start_time));
        row.Rate = safe(rate);
        row.Date = date ? new Date(date).toISOString().slice(0,10) : '';
        // Prefer client-side distance computed from locations with the same
        // accuracy filter as the mobile app; if that can't be computed
        // (e.g. bad/missing data), fall back to backend total_distance so
        // we never show 0 KM for a real route.
        const computedDistance = calculateDistanceFromLocations(locations, location_accuracy);
        const exportDistance =
            typeof computedDistance === 'number' && isFinite(computedDistance)
                ? computedDistance
                : total_distance;
        row.TotalDistance = safe(exportDistance);
        row.TotalScore = safe(Math.round(total_score || 0));
        row.StationCount = arrivals_count ? arrivals_count : 0;
        
        // Process leg-by-leg data
        const arrivedStationsIds = Array.isArray(arrivals) ? arrivals.map(arrival => arrival?.station_id) : [];
        let sortedStations = []; // Initialize as empty array
        if (is_ordered) {
            sortedStations = stations ? stations.sort((a, b) => a.order - b.order) : [];
        } else {
            const arrivedStations = Array.isArray(arrivals) && Array.isArray(stations) ? 
                arrivals.sort((a, b) => a.arrival_sec - b.arrival_sec).map((arrival) => stations.find((station) => station.id === arrival.station_id)).filter(Boolean) : [];
            const notArrivedStations = Array.isArray(stations) ? 
                stations.sort((a, b) => a.order - b.order).filter((station) => arrivedStationsIds.indexOf(station.id) === -1) : [];
            sortedStations = arrivedStations.concat(notArrivedStations);
        }
        
        // Add leg data starting from leg1 (skip index 0 which might be START station)
        for(let i = 1; i < sortedStations.length; i++){
            if (arrivedStationsIds.indexOf(sortedStations[i].id) !== -1) {
                const arrival = arrivals.find(arrival => arrival.station_id === sortedStations[i].id);
                // row["leg"+i] = sortedStations[i].id; // Use station ID like in your image
                row["leg"+i+".title"] = sortedStations[i].title || '';
                row["leg"+i+".time"] = arrival.mid_time ? toTimeFormat(arrival.mid_time) : '';
                row["leg"+i+".distance"] = arrival.distance || '';
                row["leg"+i+".score"] = arrival.score || '';
            }
            else {
                // row["leg"+i] = sortedStations[i].id;
                row["leg"+i+".title"] = sortedStations[i].title || '';
                row["leg"+i+".time"] = '';
                row["leg"+i+".distance"] = '';
                row["leg"+i+".score"] = '';
            }
        }
        return row;
    });
    try {
        // Determine maximum number of legs across all activities
        let maxLegs = 0;
        activitiesForExport.forEach(activity => {
            const legKeys = Object.keys(activity).filter(key => key.startsWith('leg') && !key.includes('.'));
            maxLegs = Math.max(maxLegs, legKeys.length);
        });
        
        // Build headers dynamically
        let headers = ['UserName', 'CourseName', 'Date', 'StartTime', 'Status', 'TotalTime', 'TotalDistance', 'Rate', 'StationCount', 'TotalScore'];
        
        // Only include email and phone headers for admin users
        if (permissions === 'admin') {
            headers.splice(1, 0, 'Email', 'Phone'); // Insert after UserName
        }
        
        // Add leg headers dynamically
        for (let i = 1; i <= maxLegs; i++) {
            headers.push(`leg${i}.title`, `leg${i}.time`, `leg${i}.distance`, `leg${i}.score`);
        }
        
        jsonExport(activitiesForExport, {
            headers: headers
        }, (err, csv) => {
            if (err) {
                // Surface the error to the console for debugging
                console.error('CSV export error:', err);
                return;
            }
            var BOM = String.fromCharCode(0xFEFF);
            downloadCSV(BOM + csv, 'Score_Table');
        });
    } catch (e) {
        console.error('Export failed:', e);
    }
}

const ActivityBulkActionButtons = props => (
    <React.Fragment>
        <BulkDeleteButton {...props} />
    </React.Fragment>
);

const ActivityFilter = ({ permissions, ...props }) => (
        <Filter {...props}>
            <TextInput label="Search" source="q" alwaysOn />
            <SelectInput source="status" choices={[
                { id: 'InProgress', name: 'In Progress' },
                { id: 'Finished', name: 'Finished' },
                { id: 'DNF', name: 'DNF' },
            ]} />
            <ReferenceInput label="Site Name" 
           perPage = {10000}
            source="track__site" reference="sites"  sort={{ field: 'name', order: 'ASC' }} allowEmpty>
                <AutocompleteInput optionText="name"  placeholder={permissions !== 'admin' ? 'None' : undefined} />
            </ReferenceInput>
            { props.filterValues.track__site &&
                <ReferenceInput label="Course Name" source="track" 
                key={props.filterValues.track__site}
                reference="tracks" filter={{site: props.filterValues.track__site, page_size: 10000}} sort={{ field: 'title', order: 'ASC' }}>
                    <AutocompleteInput optionText="title" />
                </ReferenceInput>
            }
            <TextInput label="UserName" source="user__full_name" />
            <DateInput label="Date" source="date"/>
        </Filter>
    );

const ActivityPagination = props => <Pagination rowsPerPageOptions={[10, 25, 50, 100, 200]} {...props} />;

const StationArrivalsField = ({ record }) =>
    {
        const arrivedStationsIds = record.arrivals.map(arrival => arrival.station_id);
        const isTrackOrdered = record.is_ordered;
        let sortedStations;
        if (isTrackOrdered) {
            sortedStations = record.stations.sort((a, b) => a.order - b.order);
        } else {
            sortedStations = record?.arrivals
                ?.sort((a, b) => a.arrival_sec - b.arrival_sec)
                .map((arrival) => record?.stations?.find((station) => station.id === arrival.station_id))
                .filter(Boolean);
        }

        let totalScore = 0;
        return <Table aria-label="simple table">
        <TableBody>
            <TableRow>
                <TableCell>Station Name</TableCell>
                <TableCell>Total Time</TableCell>
                <TableCell>Time Between Stations</TableCell>
                <TableCell>Distance</TableCell>
                <TableCell>Score</TableCell>
            </TableRow>
            {
                sortedStations.map((station, index) => {
                let title, score;
                let arrival_sec = null;
                let mid_time = 0;
                let distance = 0;
                if (arrivedStationsIds.indexOf(station.id) !== -1) {
                    const arrival = record.arrivals?.find(arrival => arrival.station_id === station.id);
                    title = station.title;
                    arrival_sec = arrival.arrival_sec;
                    mid_time = arrival.mid_time;
                    distance = arrival.distance;
                    score = arrival.score;
                    totalScore += score || 0;
                }
                else {
                    title = station.title;
                }
                return (
                    <TableRow key={station.id}>
                        <TableCell component="th" scope="row">
                            {title}
                        </TableCell>
                        <TableCell component="th" scope="row">
                            {arrival_sec || arrival_sec === 0 ? toTimeFormat(arrival_sec) : <Close />}
                        </TableCell>
                        <TableCell component="th" scope="row">
                            {arrival_sec || arrival_sec === 0 ? toTimeFormat(mid_time) : <Close />}
                        </TableCell>
                        <TableCell component="th" scope="row">
                            {arrival_sec || arrival_sec === 0 ? <span>{distance} KM</span> : <Close />}
                        </TableCell>
                        <TableCell component="th" scope="row">
                            {arrival_sec || arrival_sec === 0 ? (score ?? 0) : <Close />}
                        </TableCell>
                    </ TableRow>
            )
            })}
        </TableBody>
</Table>
                }

const ActivityShow = props => (
    <Show
        {...props}
        /* disable the app title change when shown */
        title=" "
    >
        <SimpleShowLayout>
            {props.record.arrivals ? <StationArrivalsField {...props} /> : <CircularProgress size={20} /> }
        </SimpleShowLayout>
    </Show>
);

const formatTime = (time) => {
    if (!time) return '';
    // Remove fractional seconds if present (e.g. "18:13:52.639498" -> "18:13:52")
    const baseTime = String(time).split('.')[0];
    const [hours = '00', minutes = '00', seconds = '00'] = baseTime.split(':');
    const pad = (val) => String(val).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const toTimeFormat = (sec_num) => {
    var hours   = Math.floor(sec_num / 3600);
    var minutes = Math.floor((sec_num - (hours * 3600)) / 60);
    var seconds = Math.floor(sec_num - (hours * 3600) - (minutes * 60));

    if (hours   < 10) {hours   = "0"+hours;}
    if (minutes < 10) {minutes = "0"+minutes;}
    if (seconds < 10) {seconds = "0"+seconds;}
    return hours+':'+minutes+':'+seconds;
}

export const ActivityList = ({permissions, ...props}) => (
    <List {...props} exporter={permissions === 'admin' ? (activities) => exporter(activities, permissions) : false} title="Score Table" perPage={50} 
    bulkActionButtons={permissions === 'admin' ? <ActivityBulkActionButtons/> : []}
    filters={permissions === 'admin' && <ActivityFilter permissions={permissions} />} pagination={<ActivityPagination />} 
    sort={{ field: 'rate', order: 'ASC' }}>

        <Datagrid expand={<ActivityShow/>}>
            { permissions !== "admin" &&
                <TextField source="user_full_name" label="User Name"/>
            }
            {permissions === 'admin' && 
                <ReferenceField source="user" label="User Name" reference="users">
                    <TextField source="full_name" />
                </ReferenceField>
            }
            {
             permissions === 'admin' && 
                <ReferenceField source="user" label="Email" reference="users">
                    <TextField source="email" />
                </ReferenceField>
            
            }
            {permissions === 'admin' && 
                <ReferenceField source="user" label="Phone" reference="users">
                    <TextField source="phone" />
                </ReferenceField>
            }
            { permissions !== "admin" &&
                <TextField source="track_title" sortBy="track" label="Course Name"/>
            }
            {permissions === 'admin' && 
                <TextField source="track_title" sortBy="track" label="Course Name" style={{color: "orange"}}/>
            }
            <DateField source="date"/>
            <FunctionField
                label="Start Time"
                render={record => {
                    const israelTime = toIsraelTime(record);
                    return israelTime ? israelTime : normalizeTimeToHHmmss(record.start_time || '');
                }}
            />
            <FunctionField label="Status" render={record => {
                // If hard time limit exceeded and status is InProgress, show as DNF
                if (record.status === 'InProgress' && record.track && record.track.route_max_time) {
                    const hardTimeLimitSeconds = record.track.route_max_time * 60;
                    if (record.total_time >= hardTimeLimitSeconds) {
                        return 'DNF';
                    }
                }
                // If activity is older than 24 hours and still InProgress, show as DNF
                if (record.status === 'InProgress' && record.date) {
                    try {
                        const activityDate = new Date(record.date);
                        const now = new Date();
                        const hoursDiff = (now.getTime() - activityDate.getTime()) / (1000 * 60 * 60);
                        if (hoursDiff >= 24) {
                            return 'DNF';
                        }
                    } catch (e) {
                        // ignore parse errors and fall back to original status
                    }
                }
                return record.status;
            }} />
            <FunctionField
                label="Total Distance"
                render={record => {
                    const distance = calculateDistanceFromLocations(
                        record.locations,
                        record.location_accuracy
                    );
                    const value =
                        typeof distance === 'number' && isFinite(distance)
                            ? distance
                            : record.total_distance || 0;
                    return `${value} KM`;
                }}
            />
            <FunctionField label="Total Time" source="total_time" render={record => toTimeFormat(record.total_time) } />
            <FunctionField label="Total Score" source="total_score" render={record => Math.round(record.total_score || 0) } />
            <TextField source="rate" style={{color: "orange"}}/>
            {/* <FunctionField label="Station Count" render={record => record.arrivals_count ? record.arrivals_count : record?.arrivals ? record.arrivals.filter((arrival) => record?.stations.find(station => station.id == arrival.station_id).title !== 'FINISH' && record?.stations.find(station => station.id == arrival.station_id).title !== 'START').length : 0 } /> */}
            <FunctionField label="Station Count" render={record => record.arrivals_count ? record.arrivals_count : 0 } />
            {permissions === 'admin' && 
                <DeleteButton mutationMode={"pessimistic"} />
            }
        </Datagrid>
    </List>
);